/**
 * External Auth Pipeline
 *
 * A generic, declarative system for orchestrating external service authentication.
 * Each service (RESO, Promote, etc.) declares its auth check as an ExternalAuthCheck
 * config object. The pipeline runs enabled checks in sequence, handling redirects
 * and error recovery uniformly.
 */

export interface ExternalAuthCheck {
  /** Identifier for logging and diagnostics (e.g. 'reso', 'promote') */
  name: string;
  /** Whether this check should run — typically gated by tenant config */
  enabled: boolean;
  /** Probe the service session. Return true if authenticated, false if a redirect was initiated. */
  checkSession: () => Promise<boolean>;
  /** Session storage key used to detect redirect-in-progress */
  sessionStorageKey: string;
  /** User-facing message shown while this check is running */
  loadingMessage: string;
}

export interface PipelineCallbacks {
  /** Called when the pipeline begins processing a check */
  onCheckStart: (check: ExternalAuthCheck) => void;
  /** Structured logging */
  log: (source: string, message: string, level?: 'info' | 'warn' | 'error') => void;
}

/**
 * Runs an ordered list of external auth checks.
 *
 * For each enabled check:
 * 1. Calls `checkSession()`
 * 2. If true  → clears the session storage flag and moves to the next check
 * 3. If false → the check initiated a redirect; pipeline stops
 * 4. If throws + session storage flag set → fatal (redirect was already attempted)
 * 5. If throws + no flag → first-time failure, pipeline stops
 *
 * @returns true if all checks passed, false if a redirect is in progress
 */
export async function runExternalAuthPipeline(
  checks: ExternalAuthCheck[],
  callbacks: PipelineCallbacks,
): Promise<boolean> {
  for (const check of checks) {
    if (!check.enabled) {
      callbacks.log(
        'AuthPipeline',
        `Skipping ${check.name} (not enabled for this tenant)`,
      );
      continue;
    }

    callbacks.onCheckStart(check);
    callbacks.log('AuthPipeline', `Checking ${check.name} authentication`);

    const redirectInProgress = sessionStorage.getItem(check.sessionStorageKey);

    try {
      const authenticated = await check.checkSession();

      if (authenticated) {
        sessionStorage.removeItem(check.sessionStorageKey);
        callbacks.log(
          'AuthPipeline',
          `${check.name} authentication complete`,
        );
      } else {
        callbacks.log(
          'AuthPipeline',
          `${check.name} initiating redirect`,
          'warn',
        );
        return false;
      }
    } catch (error) {
      if (redirectInProgress) {
        callbacks.log(
          'AuthPipeline',
          `${check.name} authentication failed after redirect attempt`,
          'error',
        );
        throw new Error(
          `${check.name} authentication failed after redirect`,
        );
      }

      callbacks.log(
        'AuthPipeline',
        `${check.name} authentication requires redirect`,
        'warn',
      );
      return false;
    }
  }

  return true;
}
